import {
  HeartHandshake, Moon, Clock, Home, Brain, Activity, Users,
  ShieldCheck, CalendarClock, HandHeart, ClipboardList,
  RefreshCcw, BadgeCheck, Wallet
} from 'lucide-react';

export const business = {
  name: "Mom's Best Friend",
  nameLine2: 'Senior Care',
  phone: '(214) 239-4339',
  phoneHref: 'tel:+12142394339',
  email: 'hello@mbfseniorcare.com',
  area: 'Dallas \u2013 Fort Worth, TX',
  since: 2009,
  rating: '5.0',
  reviewCount: '120+',
  familiesServed: '2,500+',
};

export const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'How It Works', href: '#how' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Contact', href: '#contact' },
];

// Promo video — paste the embed URL (e.g. https://player.vimeo.com/video/895697217) to go live.
export const videoEmbedUrl = '';
export const videoPoster = 'https://mbfseniorcare.com/wp-content/uploads/2026/07/shutterstock_1222015342-scaled-1.jpg';
export const freedomImage = '/freedom-img.png';

export const businessStats = [
  { value: '15+', label: 'Years in Household Staffing' },
  { value: '2009', label: 'Serving Seniors Since' },
  { value: '5.0', label: 'Star-Rated on Google & Facebook' },
  { value: '10-Step', label: 'Caregiver Screening Process' },
];

export const heroGallery = [
  '/gen/hero1.jpeg',
  '/gen/hero2.jpeg',
  '/gen/hero3.jpeg',
  '/gen/hero4.jpeg',
  '/gen/hero5.jpeg',
  '/gen/hero6.jpeg',
];

export const heroImage = 'https://images.unsplash.com/photo-1658632302217-984d432b4d38';

export const aboutImages = {
  primary: 'https://images.unsplash.com/photo-1762955913084-96ea3f986468',
  secondary: 'https://images.unsplash.com/photo-1758691031158-8d19bc43fcf8',
};

export const teamPhoto = '/team-photo.webp';

export const whyImage = '/gen/why_tile.jpeg';

const IMG = 'https://mbfseniorcare.com/wp-content/uploads/2026/07/';

export const services = [
  {
    id: 'personal',
    no: '01',
    icon: HandHeart,
    title: 'Personal Home Care',
    short: 'Dignified help with bathing, dressing & daily living.',
    image: IMG + 'shutterstock_1222015342-scaled-1.jpg',
    desc: 'Respectful, boundary-aware assistance with the tasks of daily living \u2014 so your loved one stays comfortable, clean, and confident at home.',
    included: ['Bathing, dressing & grooming', 'Mobility & transfer assistance', 'Toileting & continence support', 'Meals, housekeeping & reminders'],
  },
  {
    id: '24hr',
    no: '02',
    icon: Clock,
    title: '24-Hour Home Care',
    short: 'Continuous, around-the-clock support day and night.',
    image: IMG + 'shutterstock_495577159-scaled-1.jpg',
    desc: 'True continuous coverage with rotating shifts or a live-in arrangement, tailored to exactly how your family needs support \u2014 so your loved one is never alone.',
    included: ['Rotating & live-in shifts', 'Continuous coverage, no gaps', 'Immediate response any hour', 'Day & night safety and comfort'],
  },
  {
    id: 'overnight',
    no: '03',
    icon: Moon,
    title: 'Overnight Care',
    short: 'Restful nights with an awake caregiver nearby.',
    image: IMG + 'Fort-Worth-Caregiver-Overnight-Care-scaled-2.jpg',
    desc: 'An awake or sleepover caregiver through the night for safety, comfort, and peace of mind \u2014 so the whole family can finally rest easy.',
    included: ['Awake overnight caregivers', 'Nighttime bathroom assistance', 'Fall & wandering prevention', 'Morning routine support'],
  },
  {
    id: 'home-care',
    no: '04',
    icon: Home,
    title: 'Home Care',
    short: 'Warm companionship and a helping hand at home.',
    image: IMG + 'Home-Care-1.jpg',
    desc: 'Meaningful company and everyday help around the house from a friendly, familiar face who genuinely enjoys spending time with your loved one.',
    included: ['Conversation & engaging activities', 'Meal preparation & light housekeeping', 'Errands & transportation', 'Friendly daily check-ins'],
  },
  {
    id: 'dementia',
    no: '05',
    icon: Brain,
    title: 'Dementia Home Care',
    short: 'Patient, routine-based support for memory changes.',
    image: IMG + 'shutterstock_1648537423-scaled-1.jpg',
    desc: 'Non-medical, routine-based care from caregivers experienced with dementia \u2014 focused on calm, familiarity, gentle redirection, and dignity.',
    included: ['Consistent, familiar caregivers', 'Structured daily routines', 'Redirection & engagement', 'Safe, supportive environment'],
  },
  {
    id: 'alzheimers',
    no: '06',
    icon: HeartHandshake,
    title: "Alzheimer's Home Care",
    short: 'Compassionate, specialized support at every stage.',
    image: IMG + 'shutterstock_1729828120-scaled-1.jpg',
    desc: 'Experienced, compassionate care for those living with Alzheimer\u2019s \u2014 protecting dignity, easing anxiety, and bringing reassurance through familiar routines.',
    included: ['Caregivers trained in memory care', 'Calm, reassuring engagement', 'Care that adapts as needs change', 'Family updates & peace of mind'],
  },
  {
    id: 'parkinsons',
    no: '07',
    icon: Activity,
    title: "Parkinson's Home Care",
    short: 'Steady, attentive help with movement and routine.',
    image: IMG + 'shutterstock_1904756515-scaled-1.jpg',
    desc: 'Attentive assistance for changing mobility needs \u2014 supporting safety, confidence, and independence throughout the day.',
    included: ['Fall-risk awareness & support', 'Mobility & transfer help', 'Routine & appointment support', 'Encouraging, patient care'],
  },
  {
    id: 'memory',
    no: '08',
    icon: Users,
    title: 'Memory Care',
    short: 'A dedicated friend and advocate in any setting.',
    image: IMG + 'Fort-Worth-Caregiver-Dementia-Care-scaled-2.jpg',
    desc: 'Extra one-on-one attention in private homes, independent living, assisted living, or memory-care communities where permitted \u2014 wherever your loved one calls home.',
    included: ['One-on-one companionship', 'Support in any setting', 'Advocacy & extra attention', 'Coordination with staff'],
  },
];

export const steps = [
  {
    no: '01',
    tone: 'yellow',
    icon: HeartHandshake,
    title: 'Talk With a Care Specialist',
    desc: `Call ${business.phone} and we\u2019ll listen to understand your loved one\u2019s needs, preferences, and daily routine \u2014 no pressure, no obligation.`,
  },
  {
    no: '02',
    tone: 'green',
    icon: ClipboardList,
    title: 'Free In-Home Assessment',
    desc: 'We visit to review care needs, schedule, hobbies, and personal preferences, then craft a customized care plan that fits your family.',
  },
  {
    no: '03',
    tone: 'lilac',
    icon: HandHeart,
    title: 'Get Matched With a Caregiver',
    desc: 'We carefully match you with a caregiver who feels like family. If the fit isn\u2019t right, we\u2019ll find someone new.',
  },
];

export const promises = [
  { icon: ShieldCheck, title: 'Licensed, Bonded & Insured', desc: 'Every precaution is taken so your family is protected and can welcome care with total confidence.' },
  { icon: CalendarClock, title: 'Flexible Scheduling', desc: 'From around-the-clock care to a few visits a week, we build a schedule around your life.' },
  { icon: HeartHandshake, title: 'Caregivers Who Feel Like Family', desc: 'More than a sitter \u2014 our caregivers become trusted friends and companions.' },
  { icon: BadgeCheck, title: '10-Step Screening Process', desc: 'Every caregiver is thoroughly screened, trained, and background-checked before joining a family.' },
  { icon: RefreshCcw, title: 'A Care Plan That Adjusts', desc: 'As needs change, your care plan evolves right alongside them \u2014 always a good fit.' },
  { icon: Wallet, title: 'Help Accessing Benefits', desc: 'Our life care managers help with insurance claims and benefits to lower out-of-pocket costs.' },
];

export const signs = [
  'Risk of falling with no one nearby to help',
  'More frequent moments of confusion',
  'A declining ability to manage money',
  'Increasing risk of medication errors',
  'Struggling emotionally without company',
  'Meals, hygiene, or the home being neglected',
];

export const testimonials = [
  { name: 'Denise Springer', relation: 'Daughter of Client', quote: "Mom\u2019s Best Friend Senior Care is wonderful! They sent the absolute best person to help with my 95-year-old mom \u2014 kind, compassionate, and friendly. My mom LOVED her. I highly recommend MBF Senior Care!" },
  { name: 'Debbie Johnson', relation: 'Daughter of Client', quote: "My mother had a bad fall and I needed someone with extra eyes to keep her safe. Cynthia not only kept her safe, she engaged with my mother and always kept me updated. You have a superstar on your hands." },
  { name: 'Robert DeVargas', relation: 'Family Member', quote: "During dad\u2019s last days it was a comfort to know mom could take a break with the family because MBF provided competent, kind caregivers. Over the months, they became part of the family." },
  { name: 'Melinda Spence', relation: 'Daughter of Client', quote: "Working with Mom\u2019s Best Friend was a gift. The staff was professional and caring when we needed full-time care for my mom while we were out of town. She was in qualified, compassionate hands." },
  { name: 'Tavin K. Dillon', relation: 'Son of Client', quote: "On time, professional, and put my mind at ease caring for my mother while I was at work. 10/10!" },
  { name: 'Anh Nguyen', relation: 'Family Member', quote: "A great experience through our caregiver Cynthia \u2014 hardworking, honest, and trustworthy. She provides excellent care for my parents and has become part of our family\u2019s living activities and well-being." },
];

export const faqs = [
  { q: 'Do you provide medical or skilled nursing care?', a: 'We provide non-medical in-home care and personal assistance \u2014 companionship, personal care, and daily support. We are not a skilled home-health or medical provider, and we\u2019re always clear about what we do and don\u2019t offer.' },
  { q: 'What areas do you serve?', a: 'We proudly serve seniors and families across the Dallas\u2013Fort Worth metroplex and surrounding communities.' },
  { q: 'Is the in-home consultation really free?', a: 'Yes. Your initial in-home care assessment is free and comes with no obligation. We\u2019ll review needs and preferences and answer every question before you decide anything.' },
  { q: 'What if we don\u2019t bond with our caregiver?', a: 'Fit matters. If you don\u2019t look forward to your caregiver\u2019s visits, let us know and we\u2019ll match you with someone new.' },
  { q: 'How are your caregivers screened?', a: 'Every caregiver goes through our thorough 10-step screening, training, and background-check process before ever entering a family\u2019s home.' },
  { q: 'Can care change as needs change?', a: 'Absolutely. Your care plan is designed to evolve \u2014 from a few visits a week to overnight or 24-hour coverage \u2014 as your loved one\u2019s needs change.' },
];

export const ctaImages = [
  '/gen/cta_tall.jpeg',
  '/gen/cta_sq1.jpeg',
  '/gen/cta_sq2.jpeg',
];

export const dfwAreas = [
  'Dallas', 'Fort Worth', 'Plano', 'Frisco', 'Arlington', 'Irving',
  'McKinney', 'Richardson', 'Southlake', 'Grapevine', 'Allen', 'Denton',
  'Carrollton', 'Flower Mound', 'Colleyville', 'Highland Park',
];

// Extended per-service content for the detail pages
export const serviceDetails = {
  personal: {
    tagline: 'Dignified, boundary-aware help with the tasks of daily living.',
    overview: 'When bathing, dressing, or mobility become difficult, the right support protects both safety and dignity. Our caregivers provide respectful, non-medical personal care so your loved one stays clean, comfortable, and confident \u2014 always on their terms, right at home in Dallas and Fort Worth.',
    benefits: [
      'Safer bathing, grooming, and mobility',
      'Respectful, private, dignified assistance',
      'Lower fall risk around the home',
      'Consistent medication reminders',
    ],
    faqs: [
      { q: 'Is this medical or skilled nursing care?', a: 'No. We provide non-medical personal care and assistance with activities of daily living \u2014 not skilled nursing or medical treatment.' },
      { q: 'How do you handle privacy and dignity?', a: 'Caregivers are trained to work with clear boundaries, respect, and discretion, always centering your loved one\u2019s comfort.' },
      { q: 'Can you help with medication?', a: 'We offer medication reminders. We do not administer or prescribe medications.' },
    ],
  },
  '24hr': {
    tagline: 'Around-the-clock peace of mind, day and night.',
    overview: 'Some situations call for continuous support. Whether that means rotating shifts or a live-in arrangement, we design true 24-hour coverage tailored to how your family actually needs it \u2014 so your loved one is never alone when it matters most.',
    benefits: [
      'Continuous coverage with no gaps',
      'Rotating shifts or live-in options',
      'Immediate response at any hour',
      'Safety and comfort day and night',
    ],
    faqs: [
      { q: 'What\u2019s the difference between overnight and 24-hour care?', a: 'Overnight care covers nighttime hours (awake or sleepover), while 24-hour care provides continuous coverage across day and night, typically with rotating caregivers.' },
      { q: 'Do you offer live-in caregivers?', a: 'Yes, where operationally supported. We\u2019ll explain the options clearly so you can choose the right fit.' },
    ],
  },
  overnight: {
    tagline: 'Restful nights with an awake caregiver nearby.',
    overview: 'Nighttime can be the most worrying part of the day for families. Our overnight caregivers provide awake or sleepover support through the night \u2014 helping with bathroom trips, preventing falls and wandering, and offering reassurance \u2014 so the whole family can finally rest easy.',
    benefits: [
      'Awake overnight safety and comfort',
      'Help with nighttime bathroom needs',
      'Fall and wandering prevention',
      'A calm, well-rested morning routine',
    ],
    faqs: [
      { q: 'Is the caregiver awake all night?', a: 'We offer both awake-overnight and sleepover arrangements. We\u2019ll recommend the right option based on your loved one\u2019s needs.' },
      { q: 'Can overnight care be just a few nights a week?', a: 'Absolutely. We build the schedule around your family \u2014 a couple of nights a week or every night.' },
    ],
  },
  'home-care': {
    tagline: 'A familiar, friendly face who makes every day brighter.',
    overview: 'Loneliness is one of the quietest threats to a senior\u2019s well-being. Our home care pairs your loved one with a warm, dependable caregiver who genuinely enjoys their company \u2014 sharing conversation, favorite hobbies, and the small daily routines that make a house feel like home across Dallas and Fort Worth.',
    benefits: [
      'Reduced isolation and brighter daily mood',
      'Reliable help with errands and appointments',
      'A tidy, comfortable, well-run home',
      'Regular updates that give families peace of mind',
    ],
    faqs: [
      { q: 'How many hours a week can we start with?', a: 'Many families start with just a few hours a week and add more as they see the difference. We build the schedule around your needs.' },
      { q: 'Can the same caregiver come each visit?', a: 'Yes \u2014 consistency matters. We work hard to keep the same familiar caregiver so a real friendship can grow.' },
      { q: 'Do caregivers help with transportation?', a: 'Where offered, caregivers can drive to appointments, errands, and social outings so your loved one stays connected.' },
    ],
  },
  dementia: {
    tagline: 'Patient, routine-based support for memory changes.',
    overview: 'Caring for someone with dementia takes patience, consistency, and heart. Our caregivers are experienced with memory conditions and focus on calm routines, gentle redirection, and familiar faces \u2014 non-medical care that protects dignity and reduces anxiety at home.',
    benefits: [
      'Consistent, familiar caregivers',
      'Structured routines that reduce confusion',
      'Gentle redirection and engagement',
      'A safe, supportive home environment',
    ],
    faqs: [
      { q: 'Are caregivers trained in memory care?', a: 'Yes. Caregivers experienced with dementia focus on patience, routine, and safe, respectful engagement.' },
      { q: 'Can care adjust as the condition progresses?', a: 'Definitely. Your care plan evolves as needs change, from a few hours to overnight or 24-hour support.' },
    ],
  },
  alzheimers: {
    tagline: 'Compassionate, specialized support at every stage.',
    overview: 'Alzheimer\u2019s changes life for the whole family. Our caregivers bring experience, patience, and warmth \u2014 easing anxiety through familiar routines, meaningful engagement, and gentle, respectful care that adapts as needs change, all in the comfort of home.',
    benefits: [
      'Caregivers trained in memory care',
      'Calm, reassuring daily engagement',
      'Care that adapts at every stage',
      'Regular family updates and peace of mind',
    ],
    faqs: [
      { q: 'How is Alzheimer\u2019s care different from companionship?', a: 'It\u2019s specialized: caregivers focus on routine, safety, redirection, and reassurance techniques suited to memory loss, while still offering warm companionship.' },
      { q: 'Do you provide care in later stages?', a: 'Yes. Our care plan evolves with your loved one\u2019s needs, up to overnight and 24-hour coverage.' },
    ],
  },
  parkinsons: {
    tagline: 'Steady, attentive help with movement and routine.',
    overview: 'Changing mobility calls for attentive, encouraging support. Our caregivers help with safe movement, transfers, daily routines, and appointments \u2014 supporting confidence and independence for those living with Parkinson\u2019s and other mobility challenges.',
    benefits: [
      'Fall-risk awareness and safe transfers',
      'Support with daily routines and appointments',
      'Encouraging, patient, one-on-one care',
      'Help maintaining independence at home',
    ],
    faqs: [
      { q: 'Do you provide physical therapy?', a: 'We provide non-medical support and assistance, not therapy. We can, however, support routines recommended by your medical providers.' },
      { q: 'Can caregivers help with mobility equipment?', a: 'Yes \u2014 caregivers assist safely with walkers, transfers, and getting around the home.' },
    ],
  },
  memory: {
    tagline: 'A dedicated friend and advocate in any care setting.',
    overview: 'Even in a community setting, one-on-one attention makes a world of difference. Our caregivers provide extra companionship and advocacy in private homes, independent living, assisted living, or memory-care communities across DFW \u2014 wherever your loved one calls home.',
    benefits: [
      'One-on-one companionship anywhere',
      'Extra attention and gentle advocacy',
      'Smooth coordination with community staff',
      'Familiar support in unfamiliar settings',
    ],
    faqs: [
      { q: 'Can you provide care inside a facility?', a: 'Yes, where permitted. Many families add our one-on-one caregivers for extra attention within assisted living or memory-care communities.' },
      { q: 'Will you coordinate with facility staff?', a: 'We work respectfully alongside community staff to complement \u2014 never duplicate \u2014 their care.' },
    ],
  },
};

export const careerImage = 'https://images.unsplash.com/photo-1762955913084-96ea3f986468';

export const careerPerks = [
  { title: 'Flexible Scheduling', desc: 'Shifts that fit your life \u2014 daytime, overnight, weekends, or full-time.' },
  { title: 'Competitive Pay', desc: 'We value our caregivers with fair, dependable pay and real support.' },
  { title: 'A Caring Culture', desc: 'Join a women-owned team that treats caregivers like family, too.' },
  { title: 'Ongoing Training', desc: 'Grow your skills with training and a team that has your back.' },
];

export const careerValues = [
  'Compassion and genuine warmth',
  'Dependability and honesty',
  'Respect for every client\u2019s dignity',
  'A heart for building real relationships',
];

export const careerAvailability = ['Daytime', 'Overnight', 'Weekends', 'Full-time', 'Part-time', 'Live-in'];

// NOTE: Sample caregiver profiles (placeholder) — replace with real team members.
export const team = [
  {
    name: 'Cynthia R.',
    role: 'Senior Caregiver',
    years: '12 yrs',
    image: 'https://randomuser.me/api/portraits/women/68.jpg',
    bio: 'Beloved by families for her patience and warmth, Cynthia treats every client like her own family.',
    specialties: ['Companionship', 'Personal Care'],
  },
  {
    name: 'Angela M.',
    role: 'Memory Care Specialist',
    years: '9 yrs',
    image: 'https://randomuser.me/api/portraits/women/44.jpg',
    bio: 'Calm, steady, and endlessly kind, Angela brings routine and reassurance to those living with dementia.',
    specialties: ['Dementia Care', 'Alzheimer\u2019s'],
  },
  {
    name: 'Grace T.',
    role: 'Personal Care Aide',
    years: '7 yrs',
    image: 'https://randomuser.me/api/portraits/women/90.jpg',
    bio: 'Grace protects dignity in every task, helping clients feel confident and cared for at home.',
    specialties: ['Personal Care', 'Mobility Support'],
  },
  {
    name: 'Denise P.',
    role: 'Companion Caregiver',
    years: '10 yrs',
    image: 'https://randomuser.me/api/portraits/women/79.jpg',
    bio: 'A natural conversationalist, Denise turns everyday visits into the highlight of a senior\u2019s day.',
    specialties: ['Companionship', 'Respite Care'],
  },
];

// City-level service areas with local details (for SEO-friendly pages)
export const serviceAreas = [
  { slug: 'dallas', city: 'Dallas', image: 'https://images.unsplash.com/photo-1658632302217-984d432b4d38', blurb: 'Trusted in-home senior care across Dallas and its historic neighborhoods.', neighborhoods: ['Lakewood', 'Preston Hollow', 'Oak Cliff', 'Uptown', 'Lake Highlands'], highlights: ['Familiar caregivers who know Dallas', 'Support for downtown & suburban homes', 'Coordination with local DFW hospitals'] },
  { slug: 'fort-worth', city: 'Fort Worth', image: 'https://images.unsplash.com/photo-1758686253692-2d6921de12dd', blurb: 'Warm, dependable home care for seniors throughout Fort Worth.', neighborhoods: ['Cultural District', 'TCU Area', 'Arlington Heights', 'Ridglea', 'Wedgwood'], highlights: ['Caregivers rooted in the West side', 'Comfortable with historic & new homes', 'Flexible hourly to 24-hour care'] },
  { slug: 'plano', city: 'Plano', image: 'https://images.unsplash.com/photo-1762955911431-4c44c7c3f408', blurb: 'Compassionate senior home care for Plano families.', neighborhoods: ['Willow Bend', 'West Plano', 'Legacy', 'Los Rios'], highlights: ['Reliable suburban coverage', 'Great for multi-generational homes', 'Respite for busy family caregivers'] },
  { slug: 'frisco', city: 'Frisco', image: 'https://images.unsplash.com/photo-1758874960195-2f748297042a', blurb: 'Attentive in-home care for Frisco seniors and their families.', neighborhoods: ['Starwood', 'Newman Village', 'Panther Creek', 'Stonebriar'], highlights: ['Fast-growing community coverage', 'Personalized care plans', 'Companionship & personal care'] },
  { slug: 'mckinney', city: 'McKinney', image: 'https://images.unsplash.com/photo-1773227055624-07b515ba87c5', blurb: 'Dignified senior care throughout McKinney and Historic Downtown.', neighborhoods: ['Historic Downtown', 'Stonebridge Ranch', 'Craig Ranch', 'Adriatica'], highlights: ['Small-town warmth, big-team support', 'Mobility & Parkinson\u2019s support', 'Overnight & 24-hour options'] },
  { slug: 'arlington', city: 'Arlington', image: 'https://images.unsplash.com/photo-1658632302262-957f74b1fdd9', blurb: 'Caring in-home support for Arlington seniors.', neighborhoods: ['Dalworthington Gardens', 'Pantego', 'North Arlington', 'Viridian'], highlights: ['Between Dallas & Fort Worth', 'Flexible scheduling', 'Family relief & companionship'] },
  { slug: 'richardson', city: 'Richardson', image: 'https://images.unsplash.com/photo-1765896387403-3e6e0e44d7cc', blurb: 'Friendly, reliable home care for Richardson families.', neighborhoods: ['Canyon Creek', 'Cottonwood Heights', 'J.J. Pearce', 'Heights'], highlights: ['Caregivers who feel like family', 'Personal & companion care', 'Life Care Management available'] },
  { slug: 'southlake', city: 'Southlake', image: 'https://images.unsplash.com/photo-1758691031158-8d19bc43fcf8', blurb: 'Premium in-home senior care for Southlake families.', neighborhoods: ['Timarron', 'Carillon', 'Town Square', 'Chapel Downs'], highlights: ['Discreet, high-touch care', 'Experienced, screened caregivers', 'Around-the-clock coverage'] },
];

